import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRollbar } from '@rollbar/react'
import { Alert } from 'react-bootstrap';

import Title from './Title'
import HeaderStatusInfo from './HeaderStatusInfo'
import Loading from './Loading'
import CancelButton from './CancelButton'

import useGncHistory from '../../../hooks/useGncHistory'
import useSharePointToken from '../../../hooks/useSharePointToken'
import useAuthentication from '../../../hooks/useAuthentication'

/**
 * Shared SharePoint upload component for Documents and Records
 * 
 * @param {Object} props
 * @param {string} props.entityType - 'Document' or 'Record'
 * @param {string} props.apiPath - API path segment, e.g. 'documents' or 'records'
 * @param {string} props.statusField - Field name for status in API response, e.g. 'document_status' or 'record_status'
 */
const SharePointUpload = ({ entityType, apiPath, statusField }) => {
  const params = useParams();
  const isNew = params.objID === 'new';
  const objID = Number(params.objID);
  const projectID = params.projectID;
  const navigate = useNavigate();
  const rollbar = useRollbar();
  const { currentUser } = useAuthentication();
  
  const {
    tokenIsValid,
    encryptionKey,
    redirectToAuthUrl,
    sharePointApplicationIsValid,
    sharePointApplicationError,
  } = useSharePointToken();
  
  const {
    goBack,
  } = useGncHistory()

  const [id, setID] = useState(objID)
  const [reference, setReference] = useState('')
  const [title, setTitle] = useState('')
  const [statusName, setStatusName] = useState('')
  const [sharePointFolder, setSharePointFolder] = useState(null)
  
  const [filePaths, setFilePaths] = useState([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [hasSaved, setHasSaved] = useState(false)

  const [ isUploading, setIsUploading ] = useState(false)
  const [ uploadResult, setUploadResult ] = useState(null)
  const [ uploadError, setUploadError ] = useState(null)
  const [ authenticateSpinner, setAuthenticateSpinner ] = useState(false)
  
  useEffect(() => {
    if (isNew) {
      return
    }
    fetchObject()
  }, [])
  
  const fetchObject = () => {
    // Separate call to get the Sharepoint folder :-/
    fetch(`/v0/${apiPath}/${objID}/sharepoint_folder`, { credentials: 'same-origin' })
      .then(function(response) {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error(response.statusText)
        }
      })
      .then(function(json) {
        if(json.error) {
          throw new Error(json.error)
        }

        setSharePointFolder(json.result)
        
        fetch(`/v0/${apiPath}/${objID}`, { credentials: 'same-origin' })
          .then(function(response) {
            if (response.ok) {
              return response.json()
            } else {
              throw new Error(response.statusText)
            }
          })
          .then(function(json) {
            if(json.error) {
              throw new Error(json.error)
            }

            const obj = json.result
            setReference(obj.reference)
            setTitle(obj.title)
            setStatusName(obj[statusField]?.name)
            setFilePaths(obj.file_paths || [])
            setHasLoaded(true)
          })
          .catch(err => {
            console.log(`${entityType} fetch error`, err)
          })

      })
      .catch(err => {
        console.log(`${entityType} fetch sharepoint folder error`, err)
      })
    
  }

  const handleAuthenticate = () => {
    setAuthenticateSpinner(true);
    redirectToAuthUrl()
  }

  const handleUpload = async () => {
    if (isUploading) {
      return
    }
    
    setIsUploading(true)
    setUploadResult(null)
    setUploadError(null)
    
    try {
      const response = await fetch(
        `/v0/${apiPath}/${objID}/upload_to_sharepoint?encryption_key=${encryptionKey}`,
        {
          credentials: 'same-origin',
          method: 'POST'
        }
      )
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Upload failed')
      }
      
      const json = await response.json()
      console.log("Set upload result", json.result)
      setUploadResult(json.result)
      setHasSaved(true)
    } catch (error) {
      rollbar?.error?.("Error uploading document to SharePoint, error " + error.message, error, {
        currentUserId: currentUser?.id,
        objectType: apiPath,
        objectId: objID,
      })
      setUploadError(error.message)
    } finally {
      setIsUploading(false)
    }
  }

  const handleOpenFile = () => {
    const obj = uploadResult
    const path = obj.file_paths?.[0]
    window.open(path, "_blank", "noopener,noreferrer")
  }
  
  const pageContent = useMemo(() => {
    if (!hasLoaded) {
      return <Loading inline={true}/>
    }

    if (!sharePointApplicationIsValid) {
      return (
        <>
        <Alert variant="warning">
          In order to use SharePoint, a CADM Admin first needs to configure the SharePoint Application.
        </Alert>
        <p>
          Error given when trying to validate the SharePoint Application: {sharePointApplicationError}
        </p>
        </>
      );
    }
    
    if (!tokenIsValid) {
      return (
        <>
        <p>
          To save the {entityType.toLowerCase()} to SharePoint, you must first authenticate.
        </p>
        <CancelButton navigateOverride={() => navigate(`/portfolio/${projectID}/${apiPath}/${objID}`)} />
        <button 
            className={`btn btn-mr has-spinner btn-primary${authenticateSpinner ? ' active' : ''}`}
            role="button"
            onClick={() => handleAuthenticate() }
        >
          <span className="psc-spinner"><i className="fa fa-refresh fa-spin fa-fw"></i></span>
          Authenticate via SharePoint
        </button>
        </>
      )
    }
    
    if (hasSaved) {
      return (
        <>
        <div className="alert alert-success">
          <i className="fa fa-check-circle-o" aria-hidden="true"></i> <strong>Success!</strong> &nbsp;
          Your {entityType.toLowerCase()} has been saved to your SharePoint location: {sharePointFolder?.site_name} | {sharePointFolder?.file_path}
        </div>
        <CancelButton label="OK" navigateOverride={() => navigate(`/portfolio/${projectID}/${apiPath}`)} />
        <button 
            className="btn btn-mr btn-primary" 
            onClick={handleOpenFile}
        >
          Open
        </button>
        </>
      )
    }
    
    return (
      <>
      <div className="alert alert-info">
        <p><strong>Save Template to SharePoint</strong></p>
        <p>
          This {entityType.toLowerCase()} will be saved to your SharePoint location: {sharePointFolder?.site_name} | {sharePointFolder?.file_path}
        </p>
      </div>
      
      {filePaths[0] && !filePaths[0].includes('sharepoint.com') && (
         <div className="alert alert-warning">
           <i className="fa fa-exclamation-triangle"></i>{' '}
           This {entityType.toLowerCase()} currently has a File Location saved that is not a SharePoint link. If you choose to proceed, that link will be replaced by a SharePoint link.
         </div>
       )}
      
      <CancelButton navigateOverride={() => navigate(`/portfolio/${projectID}/${apiPath}/${objID}`)} />
      <button 
          className="btn btn-mr btn-primary" 
          onClick={handleUpload}
      >
        {isUploading ?
         <><i className="fa fa-refresh fa-spin fa-fw"></i> Uploading...</>
         : 
         <><i className="fa fa-cloud-upload"></i> Save to SharePoint</>
        }
      </button>
      
      {uploadResult &&
       <span className="text-success" style={{marginLeft: '10px'}}>
         <i className="fa fa-check"></i> Uploaded successfully
       </span>
      }
      
            {uploadError && (
               <span className="text-danger" style={{marginLeft: '10px'}}>
                 <i className="fa fa-exclamation-triangle"></i> {uploadError}
               </span>
             )}
      </>
    )
  }, [hasLoaded, tokenIsValid, hasSaved, isUploading, uploadResult, uploadError, entityType, authenticateSpinner, filePaths])
  
  return (
    <div className="wrapper wrapper-content">
      <div className="row">
        <div className="col-lg-12">
          <div className="ibox float-e-margins">
            <div className="ibox-title">
              
              <HeaderStatusInfo
                  type={entityType}
                  id={id}
                  status={statusName}
              />

              <Title
                  type={entityType}
                  reference={reference}
                  title={title}
              />
            </div>
            <div className="ibox-content">
              {pageContent}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
};

export default SharePointUpload
